import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookmarkSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { FavoriteButton } from "@/components/FavoriteButton";

const STATUS_COLORS = {
  Vigente: "bg-[#2A9D8F] text-white",
  Adjudicado: "bg-[#002FA7] text-white",
  Desierto: "bg-[#E63946] text-white",
  "Evaluación": "bg-amber-500 text-white",
  Prescindido: "bg-orange-600 text-white",
};

export default function FavoritesPage() {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchFavorites = () => {
    setLoading(true);
    api.get("/favorites")
      .then((res) => setTenders(res.data.tenders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchFavorites(); }, []);

  return (
    <div className="space-y-6" data-testid="favorites-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Mis Favoritos</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">
            Licitaciones que has marcado para seguimiento personal
          </p>
        </div>
        <p className="text-xs font-mono text-muted-foreground">
          {tenders.length} {tenders.length === 1 ? "licitación guardada" : "licitaciones guardadas"}
        </p>
      </div>

      {loading ? (
        <Card className="rounded-none">
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            Cargando...
          </CardContent>
        </Card>
      ) : tenders.length === 0 ? (
        <Card className="rounded-none border-dashed" data-testid="favorites-empty">
          <CardContent className="py-16 text-center">
            <BookmarkSimple size={48} className="mx-auto text-muted-foreground mb-4" weight="regular" />
            <p className="font-medium">Aún no tienes favoritos</p>
            <p className="text-sm text-muted-foreground mt-2">
              Marca licitaciones con el ícono <BookmarkSimple size={14} className="inline" /> para verlas aquí
            </p>
            <Button
              onClick={() => navigate("/buscar")}
              className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#002FA7]/90"
              data-testid="empty-go-search"
            >
              <MagnifyingGlass size={16} className="mr-2" /> Buscar licitaciones
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-none shadow-none border border-border/40">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-wider">NOG</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-wider">Título</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-wider">Entidad</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Monto</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-wider">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenders.map((t, i) => (
                  <TableRow
                    key={t.id}
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => navigate(`/licitacion/${t.id}`)}
                    data-testid={`fav-row-${i}`}
                  >
                    <TableCell>
                      <FavoriteButton
                        tenderId={t.id}
                        size={16}
                        onToggle={(isFav) => { if (!isFav) fetchFavorites(); }}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{t.nog}</TableCell>
                    <TableCell className="text-sm font-medium max-w-xs truncate">{t.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{t.entity_name}</TableCell>
                    <TableCell className="font-mono text-sm text-right">Q{(t.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-none text-[10px] font-mono ${STATUS_COLORS[t.status] || "bg-secondary text-foreground"}`}>
                        {t.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
